import { useState, useEffect } from 'react';
import { Check, X, Edit2, Send, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { getUploadQueue, approveQueueItem, rejectQueueItem, updateQueueItem, publishQueueItem } from '../lib/api';
import type { QueueItem } from '../types';

export default function UploadQueue() {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<QueueItem>>({});
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState<string | null>(null);

  const limit = 10;

  useEffect(() => {
    fetchQueue();
  }, [page]);

  async function fetchQueue(): Promise<void> {
    setIsLoading(true);
    try {
      const data = await getUploadQueue(page, limit);
      setItems(data.items);
      setTotal(data.total);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleApprove(itemId: string): Promise<void> {
    try {
      await approveQueueItem(itemId);
      await fetchQueue();
    } catch {
      void 0;
    }
  }

  async function handleReject(): Promise<void> {
    if (!showRejectModal) return;
    try {
      await rejectQueueItem(showRejectModal, rejectReason);
      setShowRejectModal(null);
      setRejectReason('');
      await fetchQueue();
    } catch {
      void 0;
    }
  }

  async function handlePublish(itemId: string): Promise<void> {
    try {
      await publishQueueItem(itemId);
      await fetchQueue();
    } catch {
      void 0;
    }
  }

  function startEdit(item: QueueItem): void {
    setEditingItem(item.id);
    setEditData({ category: item.category, productName: item.productName, price: item.price });
  }

  async function saveEdit(itemId: string): Promise<void> {
    try {
      await updateQueueItem(itemId, editData);
      setEditingItem(null);
      setEditData({});
      await fetchQueue();
    } catch {
      void 0;
    }
  }

  function cancelEdit(): void {
    setEditingItem(null);
    setEditData({});
  }

  function getStatusBadge(status: QueueItem['status']): React.ReactNode {
    switch (status) {
      case 'pending':
        return <span className="badge badge-warning">Pending</span>;
      case 'approved':
        return <span className="badge badge-success">Approved</span>;
      case 'rejected':
        return <span className="badge badge-destructive">Rejected</span>;
      case 'published':
        return <span className="badge badge-muted">Published</span>;
    }
  }

  const totalPages = Math.ceil(total / limit);
  const filteredItems = items.filter(
    (item) =>
      item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Upload Queue</h1>
          <p className="text-sm text-muted-foreground">Review and manage uploaded products</p>
        </div>
        <span className="text-sm text-muted-foreground">{total} items</span>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search products..."
          className="input-field pl-10"
        />
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Product
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Uploaded
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredItems.map((item) => (
                  <tr key={item.id} className="bg-surface-elevated">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={item.imageUrl}
                          alt={item.productName}
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                        {editingItem === item.id ? (
                          <input
                            type="text"
                            value={editData.productName ?? item.productName}
                            onChange={(e) => setEditData({ ...editData, productName: e.target.value })}
                            className="input-field"
                          />
                        ) : (
                          <span className="text-sm font-medium text-foreground">{item.productName}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {editingItem === item.id ? (
                        <input
                          type="text"
                          value={editData.category ?? item.category}
                          onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                          className="input-field"
                        />
                      ) : (
                        <span className="text-sm text-muted-foreground">{item.category}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingItem === item.id ? (
                        <input
                          type="number"
                          value={editData.price ?? item.price}
                          onChange={(e) => setEditData({ ...editData, price: parseFloat(e.target.value) })}
                          className="input-field w-24"
                        />
                      ) : (
                        <span className="text-sm text-foreground">
                          ${item.price.toFixed(2)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(item.status)}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(item.uploadedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {item.status === 'pending' && (
                          <>
                            {editingItem === item.id ? (
                              <>
                                <button
                                  onClick={() => saveEdit(item.id)}
                                  className="rounded-lg p-1.5 text-success hover:bg-success/10"
                                  title="Save"
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted"
                                  title="Cancel"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => startEdit(item)}
                                className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted"
                                title="Edit"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleApprove(item.id)}
                              className="rounded-lg p-1.5 text-success hover:bg-success/10"
                              title="Approve"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setShowRejectModal(item.id)}
                              className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                              title="Reject"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {item.status === 'approved' && (
                          <button
                            onClick={() => handlePublish(item.id)}
                            className="btn-primary"
                          >
                            <Send className="mr-1.5 h-4 w-4" />
                            Publish
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-secondary"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="card w-full max-w-md">
            <h3 className="text-lg font-semibold text-foreground">Reject Item</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Provide a reason for rejecting this product upload.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="input-field mt-4 min-h-24"
              placeholder="Reason for rejection..."
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectReason('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleReject} className="btn-danger">
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
